import type { ReactElement, ReactNode } from "react";

import { MISSING_SEAT_COPY } from "../../lib/firstRunEnvironment";
import type { FirstRunEnvironmentPhase } from "./useFirstRunEnvironment";

function mapEnvironmentPanelDataState(
  phase: FirstRunEnvironmentPhase | undefined,
): "confirm" | "pending" | "ready" | "failed" {
  switch (phase) {
    case "confirm":
      return "confirm";
    case "ready":
      return "ready";
    case "failed":
    case "dead-end":
      return "failed";
    case "ensuring":
    case "idle":
    default:
      return "pending";
  }
}

function environmentPanelShell(
  title: string,
  body: ReactNode,
  dataState: "confirm" | "pending" | "ready" | "failed",
): ReactElement {
  return (
    <div
      className="mt-6 rounded-lg border border-border bg-background/70 p-4"
      data-testid="first-run-environment-panel"
      data-state={dataState}
    >
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      {body}
    </div>
  );
}

/**
 * CMMD-Center/forge#3572: the account belongs to more than one CMMD workspace
 * and has not picked one for Forge yet. Ask instead of guessing. The ensure
 * control never renders until `onSelectWorkspace` names one, at which point
 * the caller's `environmentWorkspaceLabel` is set and this branch stops
 * matching.
 */
function FirstAccountSetupWorkspaceChooser(props: {
  readonly options: ReadonlyArray<{ readonly id: number; readonly name: string }>;
  readonly onSelectWorkspace: (organizationId: number) => void;
}): ReactElement {
  return environmentPanelShell(
    "Environment",
    <>
      <p className="mt-2 max-w-xl text-sm leading-5 text-muted-foreground">
        Which CMMD workspace should Forge create a cloud runtime in?
      </p>
      <div className="mt-3 flex flex-col gap-1.5">
        {props.options.map((option) => (
          <button
            key={option.id}
            type="button"
            aria-label={`Use workspace ${option.name}`}
            onClick={() => props.onSelectWorkspace(option.id)}
            className="inline-flex h-8 items-center rounded-md border border-border bg-background px-2.5 text-left text-xs font-medium text-foreground transition-colors hover:bg-accent"
          >
            {option.name}
          </button>
        ))}
      </div>
    </>,
    "confirm",
  );
}

function resolveEnvironmentPanelDetail(props: {
  readonly environmentPhase?: FirstRunEnvironmentPhase;
  readonly environmentId?: string | null;
  readonly environmentError?: string | null;
  readonly environmentWorkspaceLabel?: string | null;
}): string {
  if (props.environmentPhase === "ensuring") {
    return props.environmentWorkspaceLabel
      ? `Forge is creating the cloud runtime in ${props.environmentWorkspaceLabel}.`
      : "Forge is creating the cloud runtime for this Seat.";
  }
  if (props.environmentPhase === "failed") {
    return props.environmentError ?? "CMMD could not ensure an Environment.";
  }
  if (props.environmentId) {
    return `Environment ${props.environmentId} is ready.`;
  }
  return "Forge is checking this Seat for an existing Environment.";
}

export interface FirstAccountSetupEnvironmentPanelProps {
  readonly environmentPhase?: FirstRunEnvironmentPhase;
  readonly environmentId?: string | null;
  readonly environmentError?: string | null;
  readonly environmentWorkspaceLabel?: string | null;
  readonly environmentWorkspaceOptions?: ReadonlyArray<{
    readonly id: number;
    readonly name: string;
  }>;
  readonly onSelectEnvironmentWorkspace?: (organizationId: number) => void;
  readonly onEnsureEnvironment?: () => void;
}

type EnvironmentPanelView =
  | { readonly kind: "dead-end" }
  | { readonly kind: "choose"; readonly options: ReadonlyArray<{ id: number; name: string }> }
  | { readonly kind: "confirm"; readonly workspaceLabel: string }
  | { readonly kind: "status"; readonly detail: string };

/**
 * Which of the panel's four shapes applies, pulled out as a pure function so
 * the component itself is a straight dispatch rather than nested branching
 * (CMMD-Center/forge#3572).
 */
function classifyEnvironmentPanel(
  props: FirstAccountSetupEnvironmentPanelProps,
): EnvironmentPanelView {
  if (props.environmentPhase === "dead-end") return { kind: "dead-end" };
  const options = props.environmentWorkspaceOptions ?? [];
  const isConfirming = props.environmentPhase === "confirm";
  if (isConfirming && !props.environmentWorkspaceLabel && options.length > 0) {
    return { kind: "choose", options };
  }
  if (isConfirming && props.environmentWorkspaceLabel) {
    return { kind: "confirm", workspaceLabel: props.environmentWorkspaceLabel };
  }
  return { kind: "status", detail: resolveEnvironmentPanelDetail(props) };
}

export function FirstAccountSetupEnvironmentPanel(
  props: FirstAccountSetupEnvironmentPanelProps,
): ReactElement {
  const view = classifyEnvironmentPanel(props);
  if (view.kind === "dead-end") {
    return environmentPanelShell(
      "Seat required",
      <p className="mt-2 max-w-xl text-sm leading-5 text-muted-foreground">{MISSING_SEAT_COPY}</p>,
      mapEnvironmentPanelDataState(props.environmentPhase),
    );
  }
  if (view.kind === "choose") {
    return (
      <FirstAccountSetupWorkspaceChooser
        options={view.options}
        onSelectWorkspace={(organizationId) => props.onSelectEnvironmentWorkspace?.(organizationId)}
      />
    );
  }
  if (view.kind === "confirm") {
    const workspaceLabel = view.workspaceLabel;
    return environmentPanelShell(
      "Environment",
      <>
        <p className="mt-2 max-w-xl text-sm leading-5 text-muted-foreground">
          Forge has not created a Forge cloud server yet. Setup creates one in {workspaceLabel}.
        </p>
        <button
          type="button"
          onClick={() => props.onEnsureEnvironment?.()}
          className="mt-3 inline-flex h-7 items-center rounded-md border border-border bg-background px-2.5 text-xs font-medium text-foreground transition-colors hover:bg-accent"
          data-testid="first-run-environment-ensure"
        >
          {`Create Forge cloud server in ${workspaceLabel}`}
        </button>
      </>,
      "confirm",
    );
  }
  return environmentPanelShell(
    "Environment",
    <p className="mt-2 max-w-xl text-sm leading-5 text-muted-foreground">{view.detail}</p>,
    mapEnvironmentPanelDataState(props.environmentPhase),
  );
}
